import { IApiBaseProps } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { createUseFetchSearchFn } from '@common/search.services';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponse } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IRevision } from '@revision/revision.models';
import {
  IProjectDesignModificationSearchFilter,
  IProjectDesignModification,
  IProjectDesignModificationSearchResult,
  IUpdateProjectDesignModificationRequest,
  IUpdateProjectDesignModificationStatusRequest,
  IApproveProjectDesignModificationRequest,
  IProjectDesignModificationOptionForDesignerTransmittalRequest,
  ISearchDesignModificationDrawingRequest,
  ICreateProjectDesignModificationRequest,
  EDesignModificationIssueRole,
  IReassignCheckerForProjectDesignModificationRequest,
  IProjectDesignModificationOptionForDesignerTransmittal,
} from './designModification.models';
import {
  createAndIssueDesignModificationAsDesigner,
  createAndIssueDesignModificationAsSrp,
  createAndIssueDesignModificationAsTwc,
  createDesignModificationAsync,
  getDesignModificationAsync,
  getDesignModificationOptionForDesignerTransmittalAsync,
  getDesignModificationOptionsForLoadingRemovalCertAsync,
  getDesignModificationRevisionAsync,
  issueDesignModificationAsDesigner,
  issueDesignModificationAsSrp,
  issueDesignModificationAsTwc,
  postDesignModificationRevisionAsync,
  putDesignModificationApproveAndCheckedAsLambethAsync,
  putDesignModificationApproveAsync,
  putDesignModificationAsync,
  putDesignModificationCancelAsync,
  putDesignModificationReassignCheckerAsync,
  putDesignModificationRequestEndorsementAsync,
  searchDesignModificationAsync,
} from './designModification.services';

export const useFetchDesignModificationSearch = createUseFetchSearchFn<
  IProjectDesignModificationSearchFilter,
  IProjectDesignModificationSearchResult
>({
  queryName: FETCH_QUERIES_NAME.DESIGN_MODIFICATION_SEARCH,
  searchAsyncFn: searchDesignModificationAsync,
});

export const useFetchDesignModification = (props: IApiBaseProps, id?: number, enable = true) => {
  return useQuery<IProjectDesignModification | undefined, AxiosErrorResponse>(
    [props.projectId, id, FETCH_QUERIES_NAME.DESIGN_MODIFICATION_DETAIL],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getDesignModificationAsync(id, props);
      return results.item;
    },
    {
      enabled: !!id && isValidApiBaseProps(props) && enable,
    },
  );
};

export interface ICreateProjectDesignModificationRequestProps {
  request: ICreateProjectDesignModificationRequest;
  issueRole?: EDesignModificationIssueRole;
}

export const useCreateDesignModification = () => {
  return useMutation<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    ICreateProjectDesignModificationRequestProps,
    unknown
  >([MUTATION_KEYS.CREATE_DESIGN_MODIFICATION], async (props: ICreateProjectDesignModificationRequestProps) => {
    if (props.issueRole === EDesignModificationIssueRole.srp) {
      const result = await createAndIssueDesignModificationAsSrp(props.request);
      return result;
    }
    if (props.issueRole === EDesignModificationIssueRole.twc) {
      const result = await createAndIssueDesignModificationAsTwc(props.request);
      return result;
    }
    if (props.issueRole === EDesignModificationIssueRole.designer) {
      const result = await createAndIssueDesignModificationAsDesigner(props.request);
      return result;
    }
    const result = await createDesignModificationAsync(props.request);
    return result;
  });
};

export const useFetchDesignModificationRevision = (props: IApiBaseProps, id?: number, enable = true) => {
  return useQuery<IRevision[] | undefined, AxiosErrorResponse>(
    [props.projectId, id, FETCH_QUERIES_NAME.DESIGN_MODIFICATION_REVISIONS],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getDesignModificationRevisionAsync(id, props);
      return results.items;
    },
    {
      enabled: !!id && isValidApiBaseProps(props) && enable,
    },
  );
};

export const useCreateDesignModificationRevision = () => {
  return useMutation<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    { id: number; projectId: number },
    unknown
  >([MUTATION_KEYS.CREATE_DESIGN_MODIFICATION_REVISION], async (props: { id: number; projectId: number }) => {
    const result = await postDesignModificationRevisionAsync(props.id, { projectId: props.projectId });
    return result;
  });
};

export const useUpdateDesignModification = () => {
  return useMutation<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    IUpdateProjectDesignModificationRequest,
    unknown
  >([MUTATION_KEYS.UPDATE_DESIGN_MODIFICATION], async (request: IUpdateProjectDesignModificationRequest) => {
    const result = await putDesignModificationAsync(request.id, request);
    return result;
  });
};

export const useApproveAndCheckedAsLambethDesignModification = () => {
  return useMutation<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    IApproveProjectDesignModificationRequest,
    unknown
  >(
    [MUTATION_KEYS.APPROVE_AND_CHECKED_DESIGN_MODIFICATION_AS_LAMBETH],
    async (props: IApproveProjectDesignModificationRequest) => {
      const result = await putDesignModificationApproveAndCheckedAsLambethAsync(props);
      return result;
    },
  );
};

export interface IApproveProjectDesignModificationRequestProps {
  request: IApproveProjectDesignModificationRequest;
  issueRole?: EDesignModificationIssueRole;
}

export const useApproveDesignModification = () => {
  return useMutation<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    IApproveProjectDesignModificationRequestProps,
    unknown
  >([MUTATION_KEYS.APPROVE_DESIGN_MODIFICATION], async (props: IApproveProjectDesignModificationRequestProps) => {
    if (props.issueRole === EDesignModificationIssueRole.srp) {
      const result = await issueDesignModificationAsSrp(props.request);
      return result;
    }
    if (props.issueRole === EDesignModificationIssueRole.twc) {
      const result = await issueDesignModificationAsTwc(props.request);
      return result;
    }
    if (props.issueRole === EDesignModificationIssueRole.designer) {
      const result = await issueDesignModificationAsDesigner(props.request);
      return result;
    }
    const result = await putDesignModificationApproveAsync(props.request);
    return result;
  });
};

export const useReassignCheckerDesignModification = () => {
  return useMutation<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    IReassignCheckerForProjectDesignModificationRequest
  >(
    [MUTATION_KEYS.REASSIGN_CHECKER_DESIGN_MODIFICATION],
    async (props: IReassignCheckerForProjectDesignModificationRequest) => {
      const result = await putDesignModificationReassignCheckerAsync(props);
      return result;
    },
  );
};

export const useRequestEndorsementDesignModification = () => {
  return useMutation<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    IUpdateProjectDesignModificationStatusRequest,
    unknown
  >([MUTATION_KEYS.APPROVE_DESIGN_MODIFICATION], async (request: IUpdateProjectDesignModificationStatusRequest) => {
    const result = await putDesignModificationRequestEndorsementAsync(request);
    return result;
  });
};

export const useCancelDesignModification = () => {
  return useMutation<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    IUpdateProjectDesignModificationStatusRequest,
    unknown
  >([MUTATION_KEYS.CANCEL_DESIGN_MODIFICATION], async (request: IUpdateProjectDesignModificationStatusRequest) => {
    const result = await putDesignModificationCancelAsync(request);
    return result;
  });
};

export const useFetchDesignModificationOptionForDesignerTransmittal = (
  props: IProjectDesignModificationOptionForDesignerTransmittalRequest,
  enable = true,
) => {
  return useQuery<IProjectDesignModificationOptionForDesignerTransmittal[] | undefined, AxiosErrorResponse>(
    [props.projectId, props.masterScheduleId, FETCH_QUERIES_NAME.DESIGN_MODIFICATION_OPTION_FOR_DESIGNER_TRANSMITTAL],
    async () => {
      const results = await getDesignModificationOptionForDesignerTransmittalAsync(props);
      return results.items;
    },
    {
      enabled: isPositiveNumber(props.masterScheduleId) && isValidApiBaseProps(props) && enable,
    },
  );
};

export const useFetchDesignModificationOptionsForLoadingRemovalCert = (
  props: ISearchDesignModificationDrawingRequest,
  enable = true,
) => {
  return useQuery<IProjectDesignModification[] | undefined, AxiosErrorResponse>(
    [props.ids, FETCH_QUERIES_NAME.DESIGN_MODIFICATION_OPTIONS_FOR_LOADING_REMOVAL_CERT],
    async () => {
      const results = await getDesignModificationOptionsForLoadingRemovalCertAsync(props);
      return results.items;
    },
    {
      enabled: props.ids.length > 0 && enable,
    },
  );
};
